import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";

/**
 * AdminGuard — read-only route wrapper.
 *
 * Redirects to `/admin` (the unlock screen) whenever the admin token is
 * absent from localStorage. This is a client-side gate for UX only —
 * every backend admin endpoint independently validates the bearer token,
 * so the real security boundary is server-side.
 *
 * Child pages may push to `stoicos.admin.token.invalid` via a `storage`
 * event to force logout across tabs on 401.
 */

export const ADMIN_TOKEN_KEY = "stoicos.admin.token";

export function readAdminToken() {
  try { return localStorage.getItem(ADMIN_TOKEN_KEY) || ""; } catch { return ""; }
}

export function clearAdminToken() {
  try { localStorage.removeItem(ADMIN_TOKEN_KEY); } catch { /* ignore */ }
}

export default function AdminGuard({ children }) {
  const location = useLocation();
  const [token, setToken] = useState(() => readAdminToken());

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === ADMIN_TOKEN_KEY) setToken(e.newValue || "");
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  if (!token) {
    return <Navigate to="/admin" replace state={{ from: location.pathname }} />;
  }
  return children;
}
